const {
  CATEGORY_DEFS,
  getBoolStatus,
  getWaterStatus,
  WATER_NUMERIC_THRESHOLD,
} = require("./metricDefinitions");

function isReady(sample) {
  return sample?.has_test_measurements?.toUpperCase() === "YES";
}

function sampleTime(sample) {
  const raw = sample?.sample_tested_date || sample?.response_submission_timestamp || "";
  const time = raw ? new Date(raw).getTime() : 0;
  return Number.isNaN(time) ? 0 : time;
}

function num(value) {
  if (value === null || value === undefined || value === "" || value === "NaN") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function clamp(value, min = 0, max = 100) {
  return Math.max(min, Math.min(max, value));
}

function normalizeForMatch(value) {
  return String(value || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");
}

function cloneTemplate(template) {
  return template ? JSON.parse(JSON.stringify(template)) : null;
}

function resolveTemplateName(sample, config = {}) {
  const templates = config.templates || {};
  const mapping = config.template_selection_rules?.viscosity_class_mapping || {};
  const component = String(sample?.component_type || "").trim();
  const fuel = String(sample?.vehicle_fuel_type || "").trim();
  const viscosity = String(sample?.oil_viscosity || "").trim();
  const viscosityClass = mapping[viscosity] || "30";

  if (component === "Transmission") return "ATF-PSF";
  if (component === "Gear Box") return viscosityClass === "140" ? "GEAROIL-140" : "GEAROIL-90";
  if (fuel === "Diesel") return templates[`DIESEL-${viscosityClass}`] ? `DIESEL-${viscosityClass}` : "DIESEL-40";
  if (fuel.includes("Leaded") || fuel.includes("Race Fuel")) return templates[`Leaded-${viscosityClass}`] ? `Leaded-${viscosityClass}` : "Leaded-50";
  return templates[`ENGINE-${viscosityClass}`] ? `ENGINE-${viscosityClass}` : "ENGINE-30";
}

function applyOverride(template, metricKey, threshold, source) {
  if (!template?.[metricKey] || !threshold) return;
  if (template[metricKey].warn === 0 && template[metricKey].fail === 0 && template[metricKey].pass === 0) return;
  template[metricKey] = { ...template[metricKey], ...threshold, _conditionalSource: source };
}

function getEffectiveThresholds(sample, config = {}) {
  const templateName = resolveTemplateName(sample, config);
  const template = cloneTemplate(config.templates?.[templateName]);
  if (!template) return null;

  const rules = config.active_rules || config.conditional?.active_rules || {};

  if (rules.FUEL_DILUTION) {
    const fuel = String(sample?.vehicle_fuel_type || "").toLowerCase();
    applyOverride(template, "FUEL_DILUTION", fuel.includes("diesel") ? rules.FUEL_DILUTION.diesel : rules.FUEL_DILUTION.gasoline, rules.FUEL_DILUTION.source);
  }

  if (rules.TIN && sample?.molybdenum !== null && sample?.molybdenum !== undefined) {
    const molybdenum = Number(sample.molybdenum);
    if (Number.isFinite(molybdenum)) {
      applyOverride(template, "TIN", molybdenum > 100 ? rules.TIN.molybdenum_gt_100 : rules.TIN.molybdenum_lte_100, rules.TIN.source);
    }
  }

  if (rules.OXIDATION) {
    const brand = normalizeForMatch(sample?.oil_brand);
    const brandRule = Object.entries(rules.OXIDATION.brand_thresholds || {}).find(([brandName]) =>
      brand.includes(normalizeForMatch(brandName))
    )?.[1];
    const warn = brandRule?.warn ?? rules.OXIDATION.default?.warn ?? rules.OXIDATION.warn;
    const fail = brandRule?.fail ?? rules.OXIDATION.default?.fail ?? rules.OXIDATION.default_fail;
    applyOverride(template, "OXIDATION", { pass: 0, warn, fail, type: "GT" }, rules.OXIDATION.source);
  }

  if (rules.TITANIUM) {
    const brand = normalizeForMatch(sample?.oil_brand);
    const brandRule = Object.entries(rules.TITANIUM.brand_thresholds || {}).find(([brandName]) =>
      brand.includes(normalizeForMatch(brandName))
    )?.[1];
    if (brandRule) applyOverride(template, "TITANIUM", { ...brandRule, type: "GT" }, rules.TITANIUM.source);
  }

  const learned = rules.PDF_LEARNED_CONTEXT_OVERRIDES;
  const same = (actual, expected) => !expected || normalizeForMatch(actual) === normalizeForMatch(expected);
  for (const override of [...(learned?.overrides || [])].sort((a, b) => Number(a.priority || 0) - Number(b.priority || 0))) {
    const match = override.match || {};
    const molybdenum = Number(sample?.molybdenum);
    const bandMatches = !match.conditionalInputs?.molybdenumBand || (Number.isFinite(molybdenum)
      && ((match.conditionalInputs.molybdenumBand === "gt_100") === (molybdenum > 100)));
    if (same(templateName, match.templateName) && same(sample?.component_type, match.componentType)
      && same(sample?.vehicle_fuel_type, match.fuelType) && same(sample?.oil_viscosity, match.viscosityGrade)
      && same(sample?.oil_brand, match.oilBrand) && bandMatches) {
      applyOverride(template, override.metricKey, override.threshold, learned.source || "Authoritative expert PDF evidence");
    }
  }

  return template;
}

function scoreHigherIsWorse(value, threshold) {
  const warn = num(threshold?.warn);
  const fail = num(threshold?.fail);
  if (warn === null || warn <= 0) return null;
  if (fail !== null && fail > warn) {
    if (value < warn) return clamp((value / warn) * 45);
    if (value < fail) return clamp(50 + ((value - warn) / (fail - warn)) * 35);
    return clamp(90 + ((value - fail) / fail) * 10);
  }
  return clamp((value / warn) * 70);
}

function scoreGradeBand(value, threshold) {
  const low = num(threshold?.warn);
  const high = num(threshold?.pass);
  if (low === null || high === null || low <= 0 || high <= low) return null;
  if (value >= low && value <= high) return 0;
  if (value < low) return clamp(90 + ((low - value) / low) * 10);
  const criticalHigh = high * 1.15;
  if (value <= criticalHigh) return clamp(55 + ((value - high) / (criticalHigh - high)) * 30);
  return clamp(90 + ((value - criticalHigh) / criticalHigh) * 10);
}

function particleSegmentThreshold(threshold, metricKey) {
  if (threshold?.type !== "ISO_CODE") return threshold;
  const match = String(metricKey || "").match(/^particle_count_([0-2])$/);
  if (!match) return null;
  const index = Number(match[1]);
  const goodMax = num(threshold.goodMax?.[index]);
  const failMin = num(threshold.failMin?.[index]);
  if (goodMax === null || failMin === null) return null;
  return { type: "GT", pass: 0, warn: goodMax + 1, fail: failMin };
}

function scoreMetric(metric, sample, thresholds) {
  const rawValue = sample?.[metric.key];
  if (metric.key === "water") {
    const status = getWaterStatus(rawValue);
    if (status === "na") return null;
    if (status === "normal") return { score: 0, value: rawValue, threshold: WATER_NUMERIC_THRESHOLD };
    if (status === "warn") return { score: 50, value: rawValue, threshold: WATER_NUMERIC_THRESHOLD };
    return { score: 100, value: rawValue, threshold: WATER_NUMERIC_THRESHOLD };
  }
  if (metric.kind === "bool") {
    const status = getBoolStatus(rawValue);
    if (status === "normal") return { score: 0, value: rawValue };
    if (status === "critical") return { score: 100, value: rawValue };
    return null;
  }

  const value = num(rawValue);
  if (value === null || !metric.threshKey || !thresholds?.[metric.threshKey]) return null;
  const threshold = particleSegmentThreshold(thresholds[metric.threshKey], metric.key);
  if (!threshold) return null;
  if (threshold.type === "BOOL" || (threshold.pass === 0 && threshold.warn === 0 && threshold.fail === 0)) return null;

  const score = threshold.pass > 0 && threshold.warn > 0 && threshold.fail === 0
    ? scoreGradeBand(value, threshold)
    : scoreHigherIsWorse(value, threshold);

  return score === null ? null : { score, value, threshold };
}

function categoryScore(category, sample, config, thresholdOverride = null) {
  const thresholds = thresholdOverride || getEffectiveThresholds(sample, config);
  const contributors = category.items
    .map((metric) => {
      const result = scoreMetric(metric, sample, thresholds);
      return result ? { ...metric, ...result, score: Math.round(result.score) } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score);

  if (contributors.length === 0) return { score: null, contributors: [] };
  return { score: contributors[0].score, contributors: contributors.slice(0, 3) };
}

function rawMetricSeries(category, samples) {
  return category.items
    .map((metric) => ({
      ...metric,
      values: samples
        .map((sample) => ({ sampleId: sample.lab_sample_id, date: sample.sample_tested_date || sample.response_submission_timestamp, value: sample[metric.key] }))
        .filter((point) => point.value !== null && point.value !== undefined && point.value !== ""),
    }))
    .filter((metric) => metric.values.length >= 2);
}

function buildVehicleTrend(samples, config = {}, thresholdOverridesBySampleId = {}) {
  const readySamples = [...(samples || [])].filter(isReady).sort((a, b) => sampleTime(b) - sampleTime(a));
  const isActive = readySamples.length >= 2;
  const points = readySamples.map((sample) => {
    const point = {
      sampleId: sample.lab_sample_id,
      date: sample.sample_tested_date || sample.response_submission_timestamp,
      engineMiles: sample.engine_miles,
      sampleMiles: sample.sample_miles,
    };
    CATEGORY_DEFS.forEach((category) => {
      const result = categoryScore(category, sample, config, thresholdOverridesBySampleId?.[sample.lab_sample_id]);
      point[category.id] = result.score;
      point[`${category.id}Contributors`] = result.contributors;
    });
    return point;
  });

  const categories = CATEGORY_DEFS.map((category) => {
    const latestPoint = points.find((point) => point[category.id] !== null && point[category.id] !== undefined);
    return {
      id: category.id,
      label: category.label,
      shortLabel: category.shortLabel,
      latestScore: latestPoint?.[category.id] ?? null,
      latestContributors: latestPoint?.[`${category.id}Contributors`] || [],
      rawMetrics: rawMetricSeries(category, readySamples),
    };
  });

  return { isActive, readySamples, points, categories };
}

module.exports = {
  buildVehicleTrend,
  categoryScore,
  getEffectiveThresholds,
  resolveTemplateName,
  scoreMetric,
  particleSegmentThreshold,
};
